<template>
    <v-container :fluid="true">
        <v-row>
            <v-col class="mx-auto" cols="8">
                <v-card elevation="3">
                    <v-form v-model="formData.valid" class="mb-3" validate-on="blur" @submit.prevent="updateSettings">
                        <v-card-title id="heading" class="text-start font-weight-bold">Settings</v-card-title>

                        <v-container>
                            <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                                     density="compact" variant="tonal">
                                <p v-for="error in errors">{{ error }}</p>
                            </v-alert>
                            <v-row>
                                <v-col cols="12" sm="6">
                                    <v-switch v-model="formData['parameters']['sidebar']" color="primary"
                                              density="compact" label="Keep Sidebar Visible"/>
                                </v-col>
                                <v-col cols="12" sm="6">
                                    <v-switch :model-value="darkMode" color="primary" density="compact"
                                              label="Dark mode" @update:model-value="onDarkToggle"/>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" sm="6">
                                    <v-label class="mb-2">Distance Unit</v-label>
                                    <v-radio-group v-model="formData['parameters']['distance_unit']" inline density="compact">
                                        <v-radio label="Miles (mph)" value="miles"/>
                                        <v-radio label="Kilometers (kph)" value="km"/>
                                    </v-radio-group>
                                </v-col>
                            </v-row>
                        </v-container>

                        <v-card-actions class="px-4 d-flex justify-end">
                            <v-btn color="primary" size="small" type="submit">Save</v-btn>
                        </v-card-actions>
                    </v-form>
                </v-card>

                <!-- Notifications (push preferences - per-type toggles + quiet hours) -->
                <v-card elevation="3" class="mt-4">
                    <v-card-title class="text-start font-weight-bold">Notifications</v-card-title>
                    <v-card-text v-if="loadingPrefs" class="text-center py-6">
                        <v-progress-circular color="primary" indeterminate size="24"/>
                    </v-card-text>
                    <v-container v-else class="pt-0">
                        <v-switch v-for="t in notifToggles" :key="t.key" v-model="prefs[t.key]"
                                  color="primary" density="compact" hide-details="auto"
                                  :label="t.label" :messages="t.hint" :disabled="savingPrefs"
                                  @update:model-value="(v) => savePref(t.key, v)"/>
                        <v-divider class="my-3"/>
                        <div class="text-subtitle-2 mb-2">Quiet hours</div>
                        <p class="text-caption text-medium-emphasis mb-2">Pause non-critical alerts during these hours.</p>
                        <v-row dense>
                            <v-col cols="6">
                                <v-text-field v-model="prefs.quiet_hours_start" type="time" label="Start"
                                              density="compact" hide-details @change="saveQuiet"/>
                            </v-col>
                            <v-col cols="6">
                                <v-text-field v-model="prefs.quiet_hours_end" type="time" label="End"
                                              density="compact" hide-details @change="saveQuiet"/>
                            </v-col>
                        </v-row>
                        <v-btn v-if="prefs.quiet_hours_start || prefs.quiet_hours_end" size="x-small"
                               variant="text" class="mt-1" @click="clearQuiet">Clear quiet hours</v-btn>
                    </v-container>
                </v-card>

                <!-- Plan & Usage. Owner or promoted admin only: the API grants
                     personal.trackers.planUsage in TRACKER_PROVISION, which
                     stops at the admin tier, so an adult member or a minor now
                     gets a 403 and would just see a permanently "unavailable"
                     card. -->
                <v-card v-if="canSeePlan" elevation="3" class="mt-4">
                    <v-card-title class="text-start font-weight-bold d-flex align-center">
                        Plan &amp; Usage
                        <v-chip v-if="planUsage" size="small" class="ms-2" :color="statusColor" variant="tonal">
                            {{ planStatusLabel }}
                        </v-chip>
                    </v-card-title>
                    <v-card-text v-if="loadingPlan" class="text-center py-6">
                        <v-progress-circular color="primary" indeterminate size="24"/>
                    </v-card-text>
                    <v-card-text v-else-if="planUsage">
                        <div class="mb-3">
                            <div class="text-subtitle-1 font-weight-medium">{{ planUsage.plan.name }}</div>
                            <div class="text-caption text-medium-emphasis">{{ planPriceLabel }}</div>
                        </div>
                        <v-row dense>
                            <v-col v-for="item in usageItems" :key="item.key" cols="12" sm="4">
                                <v-sheet border rounded="lg" class="pa-3 text-center">
                                    <v-icon :icon="item.icon" color="primary" class="mb-1"/>
                                    <div class="text-h6 font-weight-bold">{{ item.count }}</div>
                                    <div class="text-caption text-medium-emphasis">{{ item.label }}</div>
                                    <div class="text-caption font-weight-medium" style="color: #2e7d32">Unlimited</div>
                                </v-sheet>
                            </v-col>
                        </v-row>
                    </v-card-text>
                    <v-card-text v-else class="text-body-2 text-medium-emphasis">
                        Plan details are unavailable right now.
                    </v-card-text>
                </v-card>

                <!-- The other door to /plans. Hidden on the same condition as
                     the sidebar item, or a comped circle still gets invited to
                     buy a plan it already holds, one screen over. -->
                <v-card v-if="isOwner && !authStore.noPurchaseAvailable" elevation="3" class="mt-4">
                    <v-list-item class="py-2" @click="router.push({name: 'plans'})">
                        <template #prepend>
                            <v-icon color="primary">mdi-crown-outline</v-icon>
                        </template>
                        <v-list-item-title class="font-weight-bold text-start">See plans &amp; upgrade</v-list-item-title>
                        <v-list-item-subtitle class="text-start">More history and premium features for your circle</v-list-item-subtitle>
                        <template #append>
                            <v-icon>mdi-chevron-right</v-icon>
                        </template>
                    </v-list-item>
                </v-card>

                <v-card elevation="3" class="mt-4">
                    <v-card-title class="text-start font-weight-bold">Your data</v-card-title>
                    <v-card-text class="text-body-2">
                        Download a copy of your personal data - your profile, trackers, trips, saved places and alerts. We'll email you a link.
                    </v-card-text>
                    <v-card-actions class="px-4 pb-3 d-flex justify-end">
                        <v-btn color="primary" variant="tonal" size="small" :loading="exporting" @click="exportData">
                            Export my data
                        </v-btn>
                    </v-card-actions>
                </v-card>

                <v-card v-if="isOwner" elevation="3" class="mt-4" color="error" variant="outlined">
                    <v-card-title class="text-start font-weight-bold">Delete account</v-card-title>
                    <v-card-text class="text-body-2">
                        Closing your account disables your circle and removes access for everyone in it.
                    </v-card-text>
                    <v-card-actions class="px-4 pb-3 d-flex justify-end">
                        <v-btn color="error" variant="tonal" size="small" @click="deleteDialog = true">
                            Delete account
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>

        <v-dialog v-model="deleteDialog" max-width="420" persistent>
            <v-card rounded="lg" class="pa-2">
                <v-card-title class="text-h6">Delete your account?</v-card-title>
                <v-card-text>
                    <p class="text-body-2 text-medium-emphasis mb-3">
                        This disables your circle and everyone's access to it. Enter your password to confirm.
                    </p>
                    <v-text-field v-model="deletePassword" type="password" label="Password" variant="outlined"
                                  autocomplete="current-password"/>
                    <v-alert v-if="deleteError" type="error" variant="tonal" density="compact">{{ deleteError }}</v-alert>
                </v-card-text>
                <v-card-actions class="px-4 pb-4">
                    <v-spacer/>
                    <v-btn variant="text" @click="deleteDialog = false">Cancel</v-btn>
                    <v-btn color="error" :loading="deleting" :disabled="!deletePassword" @click="deleteAccount">
                        Delete account
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script setup>
import {computed, onMounted, reactive, ref} from "vue";
import {useRouter} from 'vue-router';
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {useAppTheme} from "@/composables/useAppTheme";

const router = useRouter();
const errors = ref([]);
const authStore = useAuthStore();
const {showSnackbar} = useSnackbarStore();
const {isDark, setTheme} = useAppTheme();
const darkMode = ref(isDark());
const onDarkToggle = (val) => {
    darkMode.value = val;
    setTheme(val ? 'dark' : 'light');
};
const isOwner = computed(() => authStore.user?.id === authStore.user?.company?.owner_id);
// Plan allowance is owner-or-promoted-admin, matching the server's
// TRACKER_PROVISION group. Read from the store rather than recomputed here, so
// a promoted co-parent is not wrongly excluded.
const canSeePlan = computed(() => authStore.isCircleAdmin);

// Plan & Usage: current plan + status + usage counts (no caps - unlimited).
const planUsage = ref(null);
const loadingPlan = ref(true);
const usageItems = computed(() => {
    const u = planUsage.value?.usage ?? {};
    return [
        {key: 'trackers', label: 'Trackers', icon: 'mdi-radar', count: u.trackers ?? 0},
        {key: 'members', label: 'Circle members', icon: 'mdi-account-group', count: u.members ?? 0},
        {key: 'zones', label: 'Safe zones', icon: 'mdi-map-marker-radius', count: u.zones ?? 0},
    ];
});
const planPriceLabel = computed(() => {
    const p = planUsage.value?.plan;
    if (!p) return '';
    if (!p.price) return 'Free';
    const cycle = {0: 'mo', 1: 'qtr', 2: 'yr'}[p.interval] ?? '';
    return `${p.currency} ${p.price}${cycle ? '/' + cycle : ''}`;
});
const planStatusLabel = computed(() => {
    const s = planUsage.value?.plan?.status ?? 'active';
    return s.charAt(0).toUpperCase() + s.slice(1);
});
const statusColor = computed(() => {
    const s = planUsage.value?.plan?.status;
    return s === 'active' ? 'success' : s === 'cancelled' ? 'error' : 'warning';
});
onMounted(() => {
    // Don't ask for something this tier may not have. Unconditionally fetching
    // plan-usage meant every adult member and minor fired a request that now
    // 403s, and then sat looking at a card that never resolves.
    if (!canSeePlan.value) {
        loadingPlan.value = false;
        return;
    }
    send('get', 'personal-trackers/plan-usage')
        .then((r) => { planUsage.value = r.data?.data ?? null; })
        .catch(() => { /* card shows an unavailable message */ })
        .finally(() => loadingPlan.value = false);
});

// --- Push notification preferences (per-type toggles + quiet hours) ---
// Only the alert families a consumer's devices actually emit are exposed. The
// API has 13 alert types (AssetAlertTypeEnum, see composables/alertTypes.js);
// light/temp/push-button/maintenance are fleet-sensor only and intentionally
// omitted. Which types each column gates:
//   movement_alerts -> 10 place arrive/leave (plus 11/12, currently disabled),
//                      evaluated on the server; what Go users mostly receive
//   geofence_alerts -> 1, a geofence stored on and checked by a hardware tracker
//   battery_alerts  -> 7, low battery AND charger connected/disconnected
// Labels only; do not remap which column a toggle writes.
const loadingPrefs = ref(true);
const savingPrefs = ref(false);
const prefs = reactive({
    movement_alerts: true,
    geofence_alerts: true,
    speed_alerts: true,
    battery_alerts: true,
    crash_alerts: true,
    quiet_hours_start: '',
    quiet_hours_end: '',
});
const notifToggles = [
    {key: 'movement_alerts', label: 'Place arrivals & departures', hint: 'When someone arrives at or leaves one of your places'},
    {key: 'geofence_alerts', label: 'Tracker geofence alerts (hardware trackers)', hint: 'When a hardware tracker enters or leaves a geofence stored on the device'},
    {key: 'speed_alerts', label: 'Speeding', hint: 'When a tracker goes over your set speed limit'},
    {key: 'battery_alerts', label: 'Battery & charging', hint: 'Low battery, and when a charger is connected or disconnected'},
    {key: 'crash_alerts', label: 'Crash detection', hint: 'Critical - we recommend keeping this on'},
];
const asBool = (v, d) => (v === undefined || v === null) ? d : (v === true || v === 1 || v === '1' || v === 'true');
const loadPrefs = () => {
    send('get', 'push-notifications/preferences')
        .then((r) => {
            const d = r.data?.data ?? {};
            prefs.movement_alerts = asBool(d.movement_alerts, true);
            prefs.geofence_alerts = asBool(d.geofence_alerts, true);
            prefs.speed_alerts = asBool(d.speed_alerts, true);
            prefs.battery_alerts = asBool(d.battery_alerts, true);
            prefs.crash_alerts = asBool(d.crash_alerts, true);
            prefs.quiet_hours_start = d.quiet_hours_start ?? '';
            prefs.quiet_hours_end = d.quiet_hours_end ?? '';
        })
        .catch(() => { /* keep on-by-default */ })
        .finally(() => loadingPrefs.value = false);
};
const savePref = (key, value) => {
    savingPrefs.value = true;
    send('post', 'push-notifications/preferences', {[key]: value})
        .catch(() => { showSnackbar("Couldn't save that", 'error'); loadPrefs(); })
        .finally(() => savingPrefs.value = false);
};
const saveQuiet = () => {
    send('post', 'push-notifications/preferences', {
        quiet_hours_start: prefs.quiet_hours_start || '',
        quiet_hours_end: prefs.quiet_hours_end || '',
    }).catch(() => showSnackbar("Couldn't save quiet hours", 'error'));
};
const clearQuiet = () => {
    prefs.quiet_hours_start = '';
    prefs.quiet_hours_end = '';
    saveQuiet();
};
onMounted(loadPrefs);

const deleteDialog = ref(false);
const deletePassword = ref('');
const deleting = ref(false);
const deleteError = ref('');
const formData = reactive({
    user: true,
    valid: false,
    parameters: {
        sidebar: !!parseInt(authStore.configs.sidebar),
        distance_unit: authStore.configs?.distance_unit || 'miles',
    },
})
const updateSettings = () => {
    if (formData.valid) {
        errors.value = [];
        send('post', `settings`, formData)
            .then((response) => {
                showSnackbar(response.data.message);
                authStore.updateAuthConfig(response.data.data);
                router.go(); // Reloads the current route
            })
            .catch(function (errorResponse) {
                if (errorResponse.response.data.errors) {
                    let validationErrors = errorResponse.response.data.errors;
                    Object.keys(validationErrors).forEach((key) => {
                        errors.value.push(validationErrors[key][0]);
                    });
                } else if (errorResponse.response.data.message) {
                    errors.value.push(errorResponse.response.data.message);
                }
                showSnackbar('Something went wrong', 'error');
                document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
            });
    }
}
const exporting = ref(false);
const exportData = () => {
    exporting.value = true;
    send('post', 'users/data/export')
        .then((response) => {
            showSnackbar(response.data?.message ?? "We'll email you a link to your data.");
        })
        .catch(() => {
            showSnackbar('Could not start the export. Please try again.', 'error');
        })
        .finally(() => exporting.value = false);
}
const deleteAccount = () => {
    deleting.value = true;
    deleteError.value = '';
    send('post', 'account/delete', {password: deletePassword.value})
        .then((response) => {
            if (response.data.success) {
                showSnackbar(response.data.message);
                router.push('/logout');
            }
        })
        .catch((error) => {
            deleteError.value = error.response?.data?.message ?? 'Something went wrong';
        })
        .finally(() => deleting.value = false);
}
</script>

<style scoped>
</style>
